import {
  AppBar,
  Avatar,
  Box,
  Container,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { Outlet, useNavigate } from "react-router-dom";
import CampusSwitcher from "./CampusSwitcher";
import { useAuth } from "../context/AuthContext";

function initialsFromUser(user) {
  if (!user) return null;
  const source = user.name || user.full_name || user.email || user.username || "";
  const parts = String(source)
    .trim()
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2);
  const out = parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
  return out || null;
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const displayName = user?.name || user?.full_name || user?.email || null;
  const initials = initialsFromUser(user);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "#0f172a",
          backgroundImage: "linear-gradient(180deg, #0f172a 0%, #111b34 100%)",
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              bgcolor: "#2563eb",
              display: "grid",
              placeItems: "center",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            U
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ color: "#fff", lineHeight: 1.1 }}>
              Unified Doc Admin
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.65)" }}
            >
              Generate notes · Consultations · Evaluations
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <CampusSwitcher />
            {initials && (
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: "#2563eb",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {initials}
                </Avatar>
                {displayName && (
                  <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#fff",
                        lineHeight: 1.1,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 180,
                      }}
                      title={displayName}
                    >
                      {displayName}
                    </Typography>
                    {user?.role && (
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.6)" }}
                      >
                        {user.role}
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            )}
            <Tooltip title="Sign out">
              <IconButton
                onClick={handleLogout}
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: 1.5,
                  "&:hover": {
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.08)",
                    borderColor: "rgba(255,255,255,0.35)",
                  },
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Outlet />
      </Container>
    </Box>
  );
}
